import { useEffect, useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import UIkit from "uikit";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import classnames from "classnames";

import { Form } from "../../../../ui";
import SearchableDropdown from "../../../common/SearchableDropdown";
import {
  MOVEMENT_TYPE_OPTIONS,
  labelFor,
  optionName,
} from "../data";

const schema = yup.object().shape({
  item_id: yup.string().required("Item is required"),
  movement_type: yup.string().required("Action is required"),
  quantity: yup
    .number()
    .typeError("Quantity must be a number")
    .positive("Quantity must be greater than 0")
    .required("Quantity is required"),
});

const defaultValues = {
  movement_type: "received",
  from_location_id: "",
  to_location_id: "",
  item_id: "",
  quantity: "",
  counted_quantity: "",
  rental_id: "",
  guest_reservation_id: "",
  work_order_id: "",
  note: "",
};

const sourceRequiredActions = [
  "transfer",
  "consumed",
  "restock_property",
  "damaged",
  "lost",
  "disposed",
  "discarded",
  "manual_adjustment",
  "count_correction",
];

const destinationRequiredActions = ["received", "transfer", "restock_property", "returned"];
const noteRequiredActions = ["lost", "damaged", "disposed", "discarded"];

const StorageStockAdjust = ({
  entry,
  locations = [],
  items = [],
  propertyOptions = [],
  reservationOptions = [],
  taskOptions = [],
  flagIsProcessing,
  onSave,
  onCancel,
}) => {
  const [showValidation, setShowValidation] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const movementType = watch("movement_type");
  const fromLocationId = watch("from_location_id");
  const toLocationId = watch("to_location_id");
  const itemId = watch("item_id");
  const rentalId = watch("rental_id");
  const guestReservationId = watch("guest_reservation_id");
  const workOrderId = watch("work_order_id");
  const needsSource = sourceRequiredActions.includes(movementType);
  const needsDestination = destinationRequiredActions.includes(movementType);
  const needsNote = noteRequiredActions.includes(movementType);

  const resetForm = useCallback(() => {
    Object.keys(defaultValues).forEach((field) => {
      setValue(field, defaultValues[field]);
    });
  }, [setValue]);

  useEffect(() => {
    clearErrors();
    setShowValidation(false);

    if (entry) {
      setValue("movement_type", "manual_adjustment");
      setValue("from_location_id", entry.location_id || "");
      setValue("to_location_id", "");
      setValue("item_id", entry.item_id || "");
      setValue("quantity", "");
      setValue("counted_quantity", "");
      setValue("rental_id", "");
      setValue("guest_reservation_id", "");
      setValue("work_order_id", "");
      setValue("note", "");
    } else {
      resetForm();
    }
  }, [entry, clearErrors, setValue, resetForm]);

  const handleRegister = useCallback(
    async (formdata) => {
      setShowValidation(true);
      const formNeedsSource = sourceRequiredActions.includes(formdata.movement_type);
      const formNeedsDestination = destinationRequiredActions.includes(formdata.movement_type);

      if (formNeedsSource && !formdata.from_location_id) {
        UIkit.notification("Select a source location.", "warning");
        return false;
      }

      if (formNeedsDestination && !formdata.to_location_id) {
        UIkit.notification("Select a destination location.", "warning");
        return false;
      }

      if (formdata.from_location_id && formdata.from_location_id === formdata.to_location_id) {
        UIkit.notification("Source and destination must be different.", "warning");
        return false;
      }

      if (noteRequiredActions.includes(formdata.movement_type) && !String(formdata.note || "").trim()) {
        UIkit.notification("Notes are required for lost, damaged, and disposed stock.", "warning");
        return false;
      }

      try {
        const payload = {
          ...formdata,
          from_location_id: formdata.from_location_id || null,
          to_location_id: formdata.to_location_id || null,
          rental_id: formdata.rental_id || null,
          guest_reservation_id: formdata.guest_reservation_id || null,
          work_order_id: formdata.work_order_id || null,
        };
        const result = await (onSave ? onSave(payload) : true);
        if (result !== false) {
          UIkit.offcanvas("#modal-storage-stock-add").hide();
          setShowValidation(false);
          reset(defaultValues);
        }
      } catch (err) {
        UIkit.notification("Failed to save stock action.", "error");
      }
    },
    [onSave, reset],
  );

  const handleInvalid = useCallback(() => {
    setShowValidation(true);
    UIkit.notification("Complete the required stock action fields.", "warning");
  }, []);

  const handleClose = useCallback(() => {
    if (onCancel) onCancel();
    setShowValidation(false);
    UIkit.offcanvas("#modal-storage-stock-add").hide();
  }, [onCancel]);

  return (
    <div
      id="modal-storage-stock-add"
      className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
      uk-offcanvas="flip: true; overlay: true"
    >
      <div className="uk-offcanvas-bar">
        <div className="hms-modals-main">
          <button
            className="uk-offcanvas-close uk-close-large"
            type="button"
            uk-close="true"
            onClick={handleClose}
          ></button>

          <div className="uk-modal-header">
            <h3 className="uk-modal-title">Stock Action</h3>
          </div>

          <div className="uk-modal-body">
            <div className="add-contact-form">
              <Form onSubmit={handleSubmit(handleRegister, handleInvalid)} data-uk-grid="true">
                <input type="hidden" {...register("rental_id")} />
                <input type="hidden" {...register("guest_reservation_id")} />
                <input type="hidden" {...register("work_order_id")} />

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Action / Reason*</label>
                  <div uk-form-custom="target: > * > span:first-child">
                    <select {...register("movement_type")}>
                      {MOVEMENT_TYPE_OPTIONS.filter((option) => option.value).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <button
                      className={classnames("uk-button uk-button-default", {
                        "uk-form-danger": errors.movement_type,
                      })}
                      type="button"
                      tabIndex="-1"
                    >
                      <span className="select-value">
                        {labelFor(MOVEMENT_TYPE_OPTIONS, movementType, "Select Action")}
                      </span>
                      <span uk-icon="icon: chevron-down" className="hms-align-right"></span>
                    </button>
                  </div>
                  {errors.movement_type && (
                    <div className="uk-text-danger uk-text-small">{errors.movement_type.message}</div>
                  )}
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Item*</label>
                  <div uk-form-custom="target: > * > span:first-child">
                    <select {...register("item_id")}>
                      <option value="">Select Item</option>
                      {items.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.sku ? `${item.name} (${item.sku})` : item.name}
                        </option>
                      ))}
                    </select>
                    <button
                      className={classnames("uk-button uk-button-default", {
                        "uk-form-danger": errors.item_id,
                      })}
                      type="button"
                      tabIndex="-1"
                    >
                      <span className="select-value">
                        {itemId ? optionName(items, itemId, "Select Item") : "Select Item"}
                      </span>
                      <span uk-icon="icon: chevron-down" className="hms-align-right"></span>
                    </button>
                  </div>
                  {errors.item_id && <div className="uk-text-danger uk-text-small">{errors.item_id.message}</div>}
                </div>

                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">From Location{needsSource ? "*" : ""}</label>
                  <div uk-form-custom="target: > * > span:first-child">
                    <select {...register("from_location_id")}>
                      <option value="">No Source</option>
                      {locations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                    <button
                      className={classnames("uk-button uk-button-default", {
                        "uk-form-danger": showValidation && needsSource && !fromLocationId,
                      })}
                      type="button"
                      tabIndex="-1"
                    >
                      <span className="select-value">
                        {fromLocationId ? optionName(locations, fromLocationId) : "No Source"}
                      </span>
                      <span uk-icon="icon: chevron-down" className="hms-align-right"></span>
                    </button>
                  </div>
                </div>

                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">To Location{needsDestination ? "*" : ""}</label>
                  <div uk-form-custom="target: > * > span:first-child">
                    <select {...register("to_location_id")}>
                      <option value="">No Destination</option>
                      {locations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                    <button
                      className={classnames("uk-button uk-button-default", {
                        "uk-form-danger": showValidation && needsDestination && !toLocationId,
                      })}
                      type="button"
                      tabIndex="-1"
                    >
                      <span className="select-value">
                        {toLocationId ? optionName(locations, toLocationId) : "No Destination"}
                      </span>
                      <span uk-icon="icon: chevron-down" className="hms-align-right"></span>
                    </button>
                  </div>
                </div>

                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Quantity*</label>
                  <input
                    className={classnames("uk-input", { "uk-form-danger": errors.quantity })}
                    type="number"
                    min="1"
                    step="1"
                    {...register("quantity")}
                  />
                  {errors.quantity && <div className="uk-text-danger uk-text-small">{errors.quantity.message}</div>}
                </div>

                <div className="uk-width-1-2@s">
                  <label className="uk-form-label">Counted Quantity</label>
                  <input className="uk-input" type="number" min="0" step="1" {...register("counted_quantity")} />
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Property</label>
                  <div className="uk-form-controls">
                    <SearchableDropdown
                      options={[
                        { label: "No Property", value: "" },
                        ...propertyOptions,
                      ]}
                      value={rentalId}
                      onChange={(value) => setValue("rental_id", value)}
                      placeholder="No Property"
                    />
                  </div>
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Reservation ID</label>
                  <div className="uk-form-controls">
                    <SearchableDropdown
                      options={[{ label: "No Reservation", value: "" }, ...reservationOptions]}
                      value={guestReservationId}
                      onChange={(value) => setValue("guest_reservation_id", value)}
                      placeholder="No Reservation"
                    />
                  </div>
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Task</label>
                  <div className="uk-form-controls">
                    <SearchableDropdown
                      options={[{ label: "No Task", value: "" }, ...taskOptions]}
                      value={workOrderId}
                      onChange={(value) => setValue("work_order_id", value)}
                      placeholder="No Task"
                    />
                  </div>
                </div>

                <div className="uk-width-1-1">
                  <label className="uk-form-label">Notes{needsNote ? "*" : ""}</label>
                  <textarea
                    className={classnames("uk-textarea", {
                      "uk-form-danger": showValidation && needsNote && !String(watch("note") || "").trim(),
                    })}
                    rows="2"
                    {...register("note")}
                  />
                </div>

                <div className="uk-width-1-1 uk-margin-top">
                  <button className="uk-button hms-btn hms-red-btn" type="submit">
                    {flagIsProcessing ? <div data-uk-spinner="ratio: 0.5;"></div> : "Save"}
                  </button>
                  <button
                    className="uk-button hms-btn uk-offcanvas-close uk-margin-small-left"
                    onClick={handleClose}
                    type="button"
                  >
                    Cancel
                  </button>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StorageStockAdjust;
